import Button from "antd/es/button"
import Popover from "antd/es/popover"
import Upload from "antd/es/upload"
import { CloudUploadIcon } from "lucide-react"
import { useState } from "react"

import AdvancedModal from "../../components/QuestionnaireWorkflowWizard/AdvancedModal"
import createSheetsJob from "../../components/QuestionnaireWorkflowWizard/createSheetsJob"
import { QuestionnaireWorkflowWizardFromFiles } from "../../components/QuestionnaireWorkflowWizard/index"
import {
  convertNumbersToXlsx,
  readFileAsync,
} from "../../components/QuestionnaireWorkflowWizard/utils"
import { useActiveGroup } from "../../contexts/useActiveGroup"
import type { FileData } from "../../documents/types"
import type { QuiltFlags } from "../../groups/types"
import useErrorPopup from "../../hooks/useErrorPopup"
import {
  MimeType,
  QUESTIONNAIRE_ASSISTANT_SHEET_MIMETYPES,
} from "../../types/mimetype"

const { Dragger } = Upload

const getAcceptableMimetypes = (flags: QuiltFlags): MimeType[] => {
  return [
    ...QUESTIONNAIRE_ASSISTANT_SHEET_MIMETYPES,
    MimeType.PDF,
    MimeType.NUMBERS,
    MimeType.NUMBERS_OLD,
    ...(flags.showDocxQuestionnaires ? [MimeType.DOCX] : []),
  ]
}

const getAcceptableMimetypesString = (flags: QuiltFlags): string =>
  flags.showDocxQuestionnaires
    ? "Excel, CSV, PDF, Numbers, or DOCX"
    : "Excel, CSV, PDF, or Numbers"

const getInvalidFileMessageOrNone = (
  mimetype: MimeType,
  acceptableMimetypes: MimeType[],
  acceptableMimetypesString: string,
): string | null => {
  if (!acceptableMimetypes.includes(mimetype)) {
    return `Invalid file type. Please upload an ${acceptableMimetypesString} file`
  }
  return null
}

export const fileDataFromFile = async (file: File): Promise<FileData> => {
  const fileContents = await readFileAsync(file)

  if (fileContents === null) {
    throw new Error("Unable to read file")
  }

  // Convert numbers to xlsx
  const fileType = file.type as MimeType
  if (fileType === MimeType.NUMBERS || fileType === MimeType.NUMBERS_OLD) {
    const xlsxFile = await convertNumbersToXlsx(file)
    const fileName = file.name.replace(/\.numbers$/, ".xlsx")
    return {
      fileContents: xlsxFile,
      fileType: MimeType.XLSX,
      fileName,
    }
  }

  return {
    fileContents,
    fileType,
    fileName: file.name,
  }
}

// TODO (ishan) pull createJob api into this file

const AddQuestionnaireWorkflowButton: React.FC = () => {
  const [files, setFiles] = useState<FileData[]>([])
  const [open, setOpen] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [advancedModalOpen, setAdvancedModalOpen] = useState(false)
  const { handleSuccess, handleError } = useErrorPopup()

  const { flags } = useActiveGroup()

  const acceptableMimetypes = getAcceptableMimetypes(flags)
  const acceptableMimetypesString = getAcceptableMimetypesString(flags)

  const handleFileWithWizard = (fileData: FileData) => {
    setFiles((prevFiles) => [...prevFiles, fileData])
    setOpen(false)
    setModalOpen(true)
    return true
  }

  const handleFileWithAdvancedWizard = (fileData: FileData) => {
    setFiles((prevFiles) => [...prevFiles, fileData])
    setOpen(false)
    setAdvancedModalOpen(true)
    return true
  }

  const parseDocumentFile = async (file: File): Promise<FileData | null> => {
    try {
      const fileData = await fileDataFromFile(file)
      handleSuccess("File parsed successfully")
      return fileData
    } catch (error) {
      handleError({ error, prefix: "Couldn't parse file" })
      return null
    }
  }

  const handleFileChange = async (file: File): Promise<boolean> => {
    if (file === undefined) {
      handleError({ error: null, message: "No file selected" })
      return false
    }
    const fileData = await parseDocumentFile(file)
    if (!fileData) {
      return false
    }
    const invalidFileMessage = getInvalidFileMessageOrNone(
      fileData.fileType,
      acceptableMimetypes,
      acceptableMimetypesString,
    )
    if (invalidFileMessage) {
      console.log("Invalid file uploaded", fileData.fileName, fileData.fileType)
      handleError({
        error: null,
        message: invalidFileMessage,
      })
      return false
    }
    if (QUESTIONNAIRE_ASSISTANT_SHEET_MIMETYPES.includes(fileData.fileType)) {
      handleFileWithWizard(fileData)
      return true
    } else if (
      fileData.fileType === MimeType.PDF ||
      (flags.showDocxQuestionnaires && fileData.fileType === MimeType.DOCX)
    ) {
      handleFileWithAdvancedWizard(fileData)
      return true
    } else {
      return false
    }
  }

  return (
    <>
      {modalOpen && (
        <QuestionnaireWorkflowWizardFromFiles
          onClose={() => {
            setModalOpen(false)
            setFiles([])
          }}
          files={files}
          handleDocumentProcessing={createSheetsJob}
          isCompletedQuestionnaire={false}
        />
      )}
      {advancedModalOpen && (
        <AdvancedModal
          fileData={files[0]}
          onClose={() => {
            setAdvancedModalOpen(false)
            setFiles([])
          }}
          handleDocumentProcessing={createSheetsJob}
          title="Advanced Options"
        />
      )}
      <Popover
        placement="bottomLeft"
        open={open}
        onOpenChange={(newOpen) => setOpen(newOpen)}
        trigger="click"
        content={
          <div className="m-4">
            <Dragger
              className="h-full"
              beforeUpload={handleFileChange}
              showUploadList={false}
            >
              <div className="p-2">
                <CloudUploadIcon
                  className="text-primary mx-auto my-4"
                  size="48"
                />
                <p className="ant-upload-text">
                  Click or drag file to this area to upload
                </p>
                <p className="ant-upload-hint">
                  {`${acceptableMimetypesString} files are supported.`}
                </p>
              </div>
            </Dragger>
          </div>
        }
      >
        <Button
          type="primary"
          onClick={() => setOpen(!open)}
          icon={<CloudUploadIcon />}
          className="h-9 font-bold"
        >
          Upload Questionnaire
        </Button>
      </Popover>
    </>
  )
}

export default AddQuestionnaireWorkflowButton
